/**
 * <pack-builder>
 *
 * Drives the "offre rentrée" pack builder: choosing a desk (main collection,
 * shared by every pack) unlocks choosing a chair/accessory (secondary
 * collection, one per pack).
 *
 * Each product is a <div class="pack-card js-product"> (see
 * snippets/pack-product-card.liquid) that starts collapsed on a "Configurer"
 * link. Opening a card spans it across the whole grid and loads its gallery and
 * variant selectors from sections/pack-product-config.liquid through the
 * Section Rendering API — a desk built from "partie_du_produit" ships well over
 * 100 KB of JSON and twenty-odd images, so rendering every configurator up
 * front would weigh the page down. Only one card per step can be open, and the
 * open card *is* the product selected for the pack. Nothing is open on load: a
 * desk has to be picked before step 2 unlocks.
 *
 * Variant selection itself stays the job of the theme's existing components
 * (snippets/variant-picker.liquid, assets/product-parts.js,
 * assets/variant-picker.js): this controller only reacts to the
 * on:variant:change events they dispatch, then drives the card price, the
 * summary panel and the cart add.
 *
 * Dependencies:
 * - product-parts.js / variant-picker.js / custom-select.js, loaded by the
 *   section itself: <script src> tags inside injected HTML never execute.
 * - window.initLazyScript (assets/main.js), used by product-parts.js.
 * - theme.routes.cartAdd (layout/theme.liquid).
 */

if (!customElements.get("pack-builder")) {
  class PackBuilder extends HTMLElement {
    constructor() {
      super();

      this.discountAmount = parseInt(this.dataset.discountAmount, 10) || 0;
      this.ctaLabel = this.dataset.ctaLabel || "";
      this.ctaLoading = this.dataset.ctaLoading || "";
      this.ctaAdded = this.dataset.ctaAdded || "";
      this.ctaError = this.dataset.ctaError || "";
      this.chooseDeskFirstText = this.dataset.chooseDeskFirst || "";
      this.selectSecondaryText = this.dataset.selectSecondary || "";
      this.configSection = this.dataset.configSection || "pack-product-config";
      this.configLoadingText = this.dataset.configLoading || "";
      this.configErrorText = this.dataset.configError || "";
      this.unavailableText = this.dataset.unavailableCombination || "";

      const configEl = this.querySelector("[data-pack-builder-config]");
      this.config = { packs: [] };
      if (configEl) {
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (err) {
          console.error("pack-builder: invalid config JSON", err);
        }
      }

      this.packOptions = [...this.querySelectorAll("[data-pack-option]")];
      this.mainStep = this.querySelector('[data-pack-step="main"]');
      this.secondarySteps = [
        ...this.querySelectorAll('[data-pack-step="secondary"]'),
      ];

      this.summaryPackName = this.querySelector("[data-summary-pack-name]");
      this.summaryMainDesc = this.querySelector("[data-summary-main-desc]");
      this.summarySecondaryTitle = this.querySelector(
        "[data-summary-secondary-title]"
      );
      this.summarySecondaryDesc = this.querySelector(
        "[data-summary-secondary-desc]"
      );
      this.summaryLineMain = this.querySelector('[data-summary-line="main"]');
      this.summaryLineSecondary = this.querySelector(
        '[data-summary-line="secondary"]'
      );
      // Vignettes du récapitulatif mobile : elles reprennent en photo ce que les
      // deux lignes ci-dessus disent en texte, l'un remplaçant l'autre selon la
      // largeur d'écran.
      this.summaryThumbs = {
        main: this.querySelector('[data-summary-thumb="main"]'),
        secondary: this.querySelector('[data-summary-thumb="secondary"]'),
      };
      this.summaryThumbLabel = this.querySelector("[data-summary-thumb-label]");
      this.summarySubtotal = this.querySelector("[data-summary-subtotal]");
      this.summaryDiscount = this.querySelector("[data-summary-discount]");
      this.summaryTotal = this.querySelector("[data-summary-total]");
      this.step2Title = this.querySelector("[data-step2-title]");
      this.step2Label = this.querySelector("[data-step2-label]");
      this.cta = this.querySelector("[data-pack-cta]");
      this.errorEl = this.querySelector("[data-pack-error]");

      this.state = {
        // Pas forcément le premier pack : la section présélectionne l'option
        // correspondant au paramètre d'URL, en amont de ce script.
        packIndex: this.getCheckedPackIndex(),
        main: null,
        secondary: null,
      };

      this.packOptions.forEach((option) => {
        const radio = option.querySelector("[data-pack-radio]");
        if (radio) {
          radio.addEventListener("change", () =>
            this.handlePackChange(option)
          );
        }
      });

      this.addEventListener("click", this.handleClick.bind(this));
      this.addEventListener("keydown", this.handleKeydown.bind(this));

      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      document.addEventListener(
        "on:variant:change",
        this.boundHandleVariantChange
      );

      if (this.cta) {
        this.cta.addEventListener("click", this.handleAddToCart.bind(this));
      }

      this.applyPackLabels(this.state.packIndex);
      this.updateSecondaryVisibility();
      this.updateSecondaryLock();
      this.updateSummary();
    }

    disconnectedCallback() {
      document.removeEventListener(
        "on:variant:change",
        this.boundHandleVariantChange
      );
    }

    getPackConfig(index) {
      return (this.config.packs || []).find((p) => p.index === index) || null;
    }

    /** Index du pack coché dans le DOM ; 0 si aucun ne l'est. */
    getCheckedPackIndex() {
      const checked = this.packOptions.find((option) => {
        const radio = option.querySelector("[data-pack-radio]");
        return radio && radio.checked;
      });
      if (!checked) return 0;

      return parseInt(checked.dataset.packIndex, 10) || 0;
    }

    /**
     * Libellés qui suivent le pack choisi. Le rendu Liquid les sert pour le
     * premier bloc : il faut donc les rejouer à l'init aussi, pas seulement au
     * changement de pack, sinon un pack présélectionné par l'URL afficherait le
     * nom et l'étape 2 du premier.
     */
    applyPackLabels(index) {
      const packConfig = this.getPackConfig(index);
      if (!packConfig) return;

      if (this.summaryPackName) {
        this.summaryPackName.textContent = packConfig.name;
      }
      if (this.summarySecondaryTitle) {
        this.summarySecondaryTitle.textContent = packConfig.step2Title;
      }
      if (this.step2Title) {
        this.step2Title.textContent = packConfig.step2Title;
      }
    }

    /* ------------------------------------------------------------------ *
     * Packs
     * ------------------------------------------------------------------ */

    handlePackChange(option) {
      const index = parseInt(option.dataset.packIndex, 10);
      if (index === this.state.packIndex) return;

      this.state.packIndex = index;
      this.state.secondary = null;

      this.packOptions.forEach((opt) => {
        opt.classList.toggle("is-active", opt === option);
      });

      // Switching pack means switching secondary collection: nothing chosen in
      // the previous one can carry over.
      this.secondarySteps.forEach((step) => {
        step
          .querySelectorAll("[data-pack-card].is-selected")
          .forEach((card) => card.classList.remove("is-selected"));
        step
          .querySelectorAll("[data-pack-card].is-open")
          .forEach((card) => this.closeCard(card));
      });

      this.updateSecondaryVisibility();

      this.applyPackLabels(index);

      this.updateSummary();
    }

    updateSecondaryVisibility() {
      this.secondarySteps.forEach((step) => {
        const index = parseInt(step.dataset.packIndex, 10);
        step.hidden = index !== this.state.packIndex;
      });
    }

    getActiveSecondaryStep() {
      return this.secondarySteps.find(
        (step) => parseInt(step.dataset.packIndex, 10) === this.state.packIndex
      );
    }

    resolveStepFromElement(el) {
      const stepEl = el.closest("[data-pack-step]");
      if (!stepEl) return null;

      const stepType = stepEl.dataset.packStep;

      if (stepType === "secondary") {
        const stepIndex = parseInt(stepEl.dataset.packIndex, 10);
        if (stepIndex !== this.state.packIndex) return null;
      }

      return { stepEl, stepType };
    }

    /* ------------------------------------------------------------------ *
     * Cards: open / close / lazy-load the configurator
     * ------------------------------------------------------------------ */

    handleClick(evt) {
      // Le changement de pack ne repose pas sur le seul enchaînement natif
      // label → radio → change : un clic un peu traîné dessus démarre une
      // sélection et n'active plus le label. On coche donc nous-mêmes.
      const packOption = evt.target.closest("[data-pack-option]");
      if (packOption && this.contains(packOption)) {
        const radio = packOption.querySelector("[data-pack-radio]");
        if (radio) radio.checked = true;
        this.handlePackChange(packOption);
        return;
      }

      const summaryThumb = evt.target.closest("[data-summary-thumb]");
      if (summaryThumb && this.contains(summaryThumb)) {
        this.scrollToStep(summaryThumb.dataset.summaryThumb);
        return;
      }

      const thumb = evt.target.closest("[data-pack-thumb]");
      if (thumb && this.contains(thumb)) {
        const card = thumb.closest("[data-pack-card]");
        if (card) {
          this.setCardMedia(card, {
            mediaId: thumb.dataset.mediaId,
            src: thumb.dataset.imageSrc,
          });
        }
        return;
      }

      // La croix vit dans le bouton titre : elle doit être traitée avant lui,
      // sinon le clic rouvrirait aussitôt la carte qu'on vient de retirer.
      const remove = evt.target.closest("[data-pack-card-remove]");
      if (remove && this.contains(remove)) {
        this.removeSelectionFrom(remove);
        return;
      }

      const trigger = evt.target.closest(
        "[data-pack-card-toggle], [data-pack-card-configure]"
      );
      if (!trigger || !this.contains(trigger)) return;

      const card = trigger.closest("[data-pack-card]");
      if (card) this.openCard(card);
    }

    handleKeydown(evt) {
      if (evt.key !== "Enter" && evt.key !== " " && evt.key !== "Spacebar") {
        return;
      }

      const remove = evt.target.closest("[data-pack-card-remove]");
      if (!remove || !this.contains(remove)) return;

      // La croix est un span role="button" (cf. snippets/pack-product-card):
      // pas d'activation native, et l'espace ferait défiler la page.
      evt.preventDefault();
      this.removeSelectionFrom(remove);
    }

    /**
     * Retrait déclenché par la croix du titre.
     * @param {Element} el - La croix cliquée.
     */
    removeSelectionFrom(el) {
      const card = el.closest("[data-pack-card]");
      if (!card) return;

      const resolved = this.resolveStepFromElement(card);
      if (!resolved) return;

      this.clearSelection(resolved.stepType, card);
    }

    /**
     * Ramène l'écran sur l'étape que désigne une vignette du récapitulatif. Ces
     * vignettes ne sont rendues qu'en mobile, où le récapitulatif est collé en
     * bas de l'écran : sans ce raccourci, revenir sur un choix demande de
     * remonter toute la page à la main.
     * @param {string} step - "main" ou "secondary".
     */
    scrollToStep(step) {
      const target =
        step === "main" ? this.mainStep : this.getActiveSecondaryStep();
      if (!target) return;

      // Un en-tête qui suit le défilement recouvrirait le titre de l'étape. La
      // variable du thème vaut 0px quand il ne colle pas.
      const stickyHeader =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--theme-sticky-header-height"
          ),
          10
        ) || 0;
      const top =
        target.getBoundingClientRect().top + window.scrollY - stickyHeader - 16;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    }

    openCard(card) {
      const resolved = this.resolveStepFromElement(card);
      if (!resolved) return;
      if (resolved.stepEl.classList.contains("is-locked")) return;

      // One open card per step: the open card is the selected product.
      resolved.stepEl
        .querySelectorAll("[data-pack-card].is-open")
        .forEach((other) => {
          if (other !== card) this.closeCard(other);
        });

      card.classList.add("is-open");

      const toggle = card.querySelector("[data-pack-card-info]");
      if (toggle) toggle.setAttribute("aria-expanded", "true");

      const container = card.querySelector("[data-pack-card-config]");
      if (container) container.hidden = false;

      const gallery = card.querySelector("[data-pack-card-gallery]");
      if (gallery && gallery.childElementCount > 0) gallery.hidden = false;

      this.loadCardConfig(card).then((loaded) => {
        if (loaded && card.classList.contains("is-open")) {
          this.registerCardSelection(card);
        }
      });
    }

    closeCard(card) {
      card.classList.remove("is-open");

      const toggle = card.querySelector("[data-pack-card-info]");
      if (toggle) toggle.setAttribute("aria-expanded", "false");

      const container = card.querySelector("[data-pack-card-config]");
      if (container) container.hidden = true;

      const gallery = card.querySelector("[data-pack-card-gallery]");
      if (gallery) gallery.hidden = true;
    }

    /**
     * Fetches and injects the card's variant selectors. Idempotent: the markup
     * is kept once loaded, so reopening a card costs nothing.
     * @param {Element} card - Card element.
     * @returns {Promise<boolean>} Whether the card is ready to be registered.
     */
    loadCardConfig(card) {
      const container = card.querySelector("[data-pack-card-config]");
      if (!container) return Promise.resolve(false);

      if (card.dataset.configLoaded === "true") return Promise.resolve(true);
      if (card.configRequest) return card.configRequest;

      // Le fragment est aussi demandé pour un produit sans option à choisir :
      // c'est lui qui porte la galerie.
      const url = `${card.dataset.productUrl}?variant=${card.dataset.startVariant}&section_id=${this.configSection}`;

      container.innerHTML = "";
      container.appendChild(this.buildNote(this.configLoadingText));

      card.configRequest = fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((html) => {
          const tmpl = document.createElement("template");
          tmpl.innerHTML = html;

          const config = tmpl.content.querySelector("[data-pack-config]");
          if (!config) throw new Error("pack-builder: no config in response");

          // The gallery goes to the card's own column, next to the selectors.
          const gallery = tmpl.content.querySelector("[data-pack-gallery]");
          const gallerySlot = card.querySelector("[data-pack-card-gallery]");
          if (gallery && gallerySlot) {
            gallerySlot.innerHTML = "";
            gallerySlot.appendChild(gallery);
            gallerySlot.hidden = !card.classList.contains("is-open");
            card.classList.add("has-gallery");
          }

          // The fragment comes from a /products/ URL, so the pickers think they
          // are on a product page: stop them from rewriting the page URL.
          config
            .querySelectorAll('variant-picker[data-update-url="true"]')
            .forEach((el) => {
              el.dataset.updateUrl = "false";
            });
          config.querySelectorAll("product-parts[data-url]").forEach((el) => {
            el.dataset.url = window.location.pathname;
          });
          // Inert once injected, and <noscript> would show up as text.
          config
            .querySelectorAll("script[src], noscript")
            .forEach((el) => el.remove());

          container.innerHTML = "";
          container.appendChild(config);

          this.readCardPrices(card);
          card.dataset.configLoaded = "true";
          return true;
        })
        .catch((err) => {
          console.error(err);
          container.innerHTML = "";
          container.appendChild(this.buildNote(this.configErrorText));
          card.configRequest = null;
          return false;
        });

      return card.configRequest;
    }

    buildNote(text) {
      const note = document.createElement("p");
      note.className = "pack-config__note";
      note.textContent = text;
      return note;
    }

    readCardPrices(card) {
      const priceEl = card.querySelector("[data-pack-config-prices]");
      if (priceEl) {
        try {
          card.variantPrices = JSON.parse(priceEl.textContent);
        } catch (err) {
          console.error("pack-builder: invalid price JSON", err);
        }
      }

      const mediaEl = card.querySelector("[data-pack-config-variant-media]");
      if (mediaEl) {
        try {
          card.variantMedia = JSON.parse(mediaEl.textContent);
        } catch (err) {
          console.error("pack-builder: invalid variant media JSON", err);
        }
      }
    }

    /**
     * Swaps the gallery's main image. Only this one image is ever loaded: the
     * thumbnails carry their own source, and a variant's photo is only fetched
     * once it lands here.
     * @param {Element} card - Card element.
     * @param {object} media - {mediaId, src} of the view to show.
     */
    setCardMedia(card, media) {
      if (!media || !media.src) return;

      const mainImage = card.querySelector("[data-pack-gallery-main]");
      if (!mainImage) return;

      if (mainImage.dataset.mediaId !== String(media.mediaId)) {
        mainImage.src = media.src;
        mainImage.dataset.mediaId = String(media.mediaId);
      }

      // La vue affichée n'est pas toujours dans la bande : la photo d'une
      // variante n'y figure pas, aucune vignette n'est alors active.
      card.querySelectorAll("[data-pack-thumb]").forEach((thumb) => {
        thumb.classList.toggle(
          "is-active",
          thumb.dataset.mediaId === String(media.mediaId)
        );
      });
    }

    /** Follows the configuration: each variant may carry its own photo. */
    setCardMediaFromVariant(card, variantId) {
      const entry = (card.variantMedia || {})[String(variantId)];
      if (!entry) return;

      this.setCardMedia(card, { mediaId: entry.id, src: entry.src });
    }

    /**
     * Price and availability of a variant, from the map shipped with the
     * configurator. Falls back to the entry variant known by the card, which is
     * all a product without options ever needs.
     */
    getPriceInfo(card, variantId) {
      const key = String(variantId);
      const prices = card.variantPrices || {};

      if (prices[key]) return prices[key];

      if (key === String(card.dataset.startVariant)) {
        return {
          price: parseInt(card.dataset.startPrice, 10) || 0,
          formatted: card.dataset.startPriceFormatted || "",
          available: card.dataset.startAvailable !== "false",
        };
      }

      return { price: 0, formatted: "", available: true };
    }

    /* ------------------------------------------------------------------ *
     * Cards: selected options
     * ------------------------------------------------------------------ */

    /**
     * Selected value of one option group, whichever picker rendered it:
     * product-parts buttons and custom-select options carry data-value, the
     * standard picker uses checked radios.
     */
    getSelectedValue(selector) {
      const active = selector.querySelector(
        '.js-option.is-selected, .js-option[aria-selected="true"], input.js-option:checked'
      );
      if (!active) return "";

      return (active.dataset.value || active.value || active.textContent || "")
        .trim();
    }

    getOptionSelectors(card) {
      return [
        ...card.querySelectorAll("[data-pack-card-config] .option-selector"),
      ];
    }

    /**
     * Mirrors the mockup's "Taille : 120*60 cm": the group label carries its
     * selected value. Colour options of the standard picker already render one,
     * kept up to date by variant-picker.js.
     */
    refreshOptionLabels(card) {
      this.getOptionSelectors(card).forEach((selector) => {
        const labelEl = selector.querySelector("legend.label, .label");
        if (!labelEl) return;
        if (labelEl.querySelector(".option-selector__label-value")) return;

        let valueEl = labelEl.querySelector("[data-pack-label-value]");
        if (!valueEl) {
          valueEl = document.createElement("span");
          valueEl.setAttribute("data-pack-label-value", "");
          valueEl.className = "pack-config__label-value";
          labelEl.appendChild(valueEl);
        }

        const value = this.getSelectedValue(selector);
        valueEl.textContent = value ? ` : ${value}` : "";
      });
    }

    getCardDescription(card) {
      const title = card.dataset.productTitle || "";
      const values = this.getOptionSelectors(card)
        .map((selector) => this.getSelectedValue(selector))
        .filter(Boolean);

      return values.length ? `${title} — ${values.join(" · ")}` : title;
    }

    /**
     * Registers a freshly opened card: the pickers only dispatch
     * on:variant:change once the shopper touches an option.
     */
    registerCardSelection(card) {
      const resolved = this.resolveStepFromElement(card);
      if (!resolved) return;

      const configEl = card.querySelector("[data-pack-config]");
      const idInput = card.querySelector('.js-product-form [name="id"]');
      const variantId =
        (configEl && configEl.dataset.variantId) ||
        (idInput && idInput.value) ||
        card.dataset.startVariant;

      const info = this.getPriceInfo(card, variantId);
      const available = configEl
        ? configEl.dataset.variantAvailable !== "false"
        : info.available !== false;

      // Rien à faire sur la galerie : le fragment est déjà rendu sur la photo de
      // la variante demandée.
      this.refreshOptionLabels(card);
      this.setCardPrice(card, info.formatted);

      this.setSelection(resolved.stepType, {
        variantId,
        price: info.price || 0,
        available,
        title: this.getCardDescription(card),
        card,
      });
    }

    handleVariantChange(evt) {
      const form = evt.detail && evt.detail.form;
      if (!form || !this.contains(form)) return;

      const card = form.closest("[data-pack-card]");
      if (!card) return;

      const resolved = this.resolveStepFromElement(card);
      if (!resolved) return;

      const variant = evt.detail.variant;

      this.refreshOptionLabels(card);

      // No variant means the shopper landed on a combination that does not
      // exist: keep the card selected but block the CTA.
      if (!variant) {
        this.setCardPrice(card, "");
        this.setSelection(resolved.stepType, {
          variantId: null,
          price: 0,
          available: false,
          title: `${card.dataset.productTitle} — ${this.unavailableText}`,
          card,
        });
        return;
      }

      const info = this.getPriceInfo(card, variant.id);
      const price = Number(variant.price) || info.price || 0;

      this.setCardPrice(card, info.formatted || this.formatMoney(price));
      this.setCardMediaFromVariant(card, variant.id);

      this.setSelection(resolved.stepType, {
        variantId: variant.id,
        price,
        available: variant.available !== false,
        title: this.getCardDescription(card),
        card,
      });
    }

    /**
     * Shows the configured price on the card. An empty value restores the
     * "à partir de X €" the card was rendered with.
     */
    setCardPrice(card, formatted) {
      const priceEl = card.querySelector("[data-pack-card-price]");
      if (!priceEl) return;

      priceEl.textContent = formatted || card.dataset.fromPrice || "";
    }

    setSelection(step, selection) {
      this.state[step] = selection;

      const group =
        step === "main" ? this.mainStep : this.getActiveSecondaryStep();
      if (group) {
        group.querySelectorAll("[data-pack-card]").forEach((card) => {
          card.classList.toggle("is-selected", card === selection.card);
        });
      }

      if (step === "main") {
        this.updateSecondaryLock();
      }

      this.updateSummary();
    }

    /**
     * Annule le choix d'une étape : la carte redevient une carte comme les
     * autres, refermée sur son « à partir de X € », et la ligne correspondante
     * du récapitulatif repasse en attente.
     * @param {string} step - "main" ou "secondary".
     * @param {?Element} card - Carte à refermer ; celle de l'état par défaut.
     */
    clearSelection(step, card) {
      const target = card || (this.state[step] && this.state[step].card);

      // Choisir un bureau déverrouille l'étape 2 : le retirer la remet donc à
      // zéro, sinon une chaise resterait retenue sous le voile.
      if (step === "main" && this.state.secondary) {
        this.clearSelection("secondary");
      }

      this.state[step] = null;

      if (target) {
        target.classList.remove("is-selected");
        this.closeCard(target);
        this.setCardPrice(target, "");
      }

      if (step === "main") {
        this.updateSecondaryLock();
      }

      this.updateSummary();
    }

    updateSecondaryLock() {
      const unlocked = Boolean(this.state.main);

      this.secondarySteps.forEach((step) => {
        step.classList.toggle("is-locked", !unlocked);

        // La collection reste affichée en aperçu sous le voile : inert la sort du
        // parcours clavier et des clics tant que l'étape est verrouillée.
        const grid = step.querySelector(".product-grid");
        if (grid) grid.inert = !unlocked;
      });

      if (this.step2Label) {
        this.step2Label.classList.toggle("is-current", unlocked);
      }
    }

    /* ------------------------------------------------------------------ *
     * Summary & cart
     * ------------------------------------------------------------------ */

    formatMoney(cents) {
      const price = (Math.max(0, cents) / 100).toFixed(2).replace(".", ",");
      return `${price}€`;
    }

    /**
     * Photo à montrer pour une sélection. La carte du produit garde ses propres
     * URL : on réutilise celle de la variante, donc une image que la galerie a
     * déjà téléchargée — la vignette est un hit de cache, pas une requête de plus.
     * @param {?object} selection - Entrée de this.state.
     * @returns {string} URL, vide si rien à montrer.
     */
    getSelectionImage(selection) {
      const card = selection && selection.card;
      if (!card) return "";

      const entry = (card.variantMedia || {})[String(selection.variantId)];
      if (entry && entry.src) return entry.src;

      // Produit sans photo par variante : la vue active de la galerie fait foi.
      // On ne lit pas .src, qui résoudrait l'URL protocol-relative de Shopify et
      // ne serait plus comparable à celles de variantMedia.
      const galleryMain = card.querySelector("[data-pack-gallery-main]");
      return (galleryMain && galleryMain.getAttribute("src")) || "";
    }

    /** Remplit une vignette du récapitulatif, ou la rend à son cadre pointillé. */
    setSummaryThumb(step, selection) {
      const thumb = this.summaryThumbs[step];
      if (!thumb) return;

      const img = thumb.querySelector("[data-summary-thumb-img]");
      const src = this.getSelectionImage(selection);

      if (!img || !src) {
        thumb.classList.remove("is-filled");
        return;
      }

      if (img.getAttribute("src") !== src) img.setAttribute("src", src);
      img.alt = (selection.card && selection.card.dataset.productTitle) || "";
      thumb.classList.add("is-filled");
    }

    updateSummary() {
      const { main, secondary } = this.state;

      if (this.summaryMainDesc) {
        this.summaryMainDesc.textContent = main ? main.title : "";
      }
      if (this.summaryLineMain) {
        this.summaryLineMain.classList.toggle(
          "pack-builder__summary-line--pending",
          !main
        );
      }

      if (this.summarySecondaryDesc) {
        if (secondary) {
          this.summarySecondaryDesc.textContent = secondary.title;
        } else if (main) {
          this.summarySecondaryDesc.textContent = this.selectSecondaryText;
        } else {
          this.summarySecondaryDesc.textContent = this.chooseDeskFirstText;
        }
      }
      if (this.summaryLineSecondary) {
        this.summaryLineSecondary.classList.toggle(
          "pack-builder__summary-line--pending",
          !secondary
        );
      }

      this.setSummaryThumb("main", main);
      this.setSummaryThumb("secondary", secondary);

      // Le cadre pointillé de la 2e vignette nomme l'étape restante, qui dépend
      // du pack : « une chaise ergonomique » ou « un accessoire de mouvement ».
      const packConfig = this.getPackConfig(this.state.packIndex);
      if (this.summaryThumbLabel && packConfig) {
        this.summaryThumbLabel.textContent = packConfig.step2Title;
      }

      const subtotal = (main ? main.price : 0) + (secondary ? secondary.price : 0);
      // La remise s'applique dès qu'un article est sélectionné, sans attendre que
      // le pack soit complet. Math.min évite un total négatif quand le sous-total
      // est inférieur au montant de la remise.
      const discount =
        this.discountAmount > 0 ? Math.min(this.discountAmount, subtotal) : 0;
      const total = subtotal - discount;

      if (this.summarySubtotal) {
        this.summarySubtotal.textContent = main ? this.formatMoney(subtotal) : "—";
      }
      // La ligne de remise reste toujours affichée ; comme le sous-total et le
      // total, elle attend qu'un article soit choisi pour afficher une valeur.
      if (this.summaryDiscount) {
        this.summaryDiscount.textContent = main
          ? `-${this.formatMoney(discount)}`
          : "—";
      }
      if (this.summaryTotal) {
        this.summaryTotal.textContent = main ? this.formatMoney(total) : "—";
      }

      if (this.cta) {
        this.cta.disabled = !this.isPackOrderable();
      }
    }

    isPackOrderable() {
      const { main, secondary } = this.state;

      return [main, secondary].every(
        (selection) =>
          selection && selection.variantId && selection.available !== false
      );
    }

    async handleAddToCart() {
      const { main, secondary } = this.state;
      if (!this.isPackOrderable() || !this.cta || this.cta.disabled) return;

      this.setErrorState(false);
      this.cta.disabled = true;
      this.cta.textContent = this.ctaLoading;

      const packConfig = this.getPackConfig(this.state.packIndex);
      const packName = packConfig ? packConfig.name : "";

      const items = [main, secondary].map((selection) => ({
        id: selection.variantId,
        quantity: 1,
        properties: { _pack: packName },
      }));

      const sectionsToUpdate = ["page-header", "cart-drawer"]
        .map((sel) => document.querySelector(sel))
        .filter((el) => el);
      const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(","),
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          let error = data.description || data.message;
          if (data.errors && typeof data.errors === "object") {
            error = Object.entries(data.errors)
              .map((item) => item[1].join(", "))
              .join(", ");
          }
          throw new Error(error || this.ctaError);
        }

        sectionsToUpdate.forEach((el) => {
          if (data.sections && data.sections[el.dataset.sectionId]) {
            el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          }
        });

        (data.items || []).forEach((item) => {
          document.dispatchEvent(
            new CustomEvent("on:cart:add", {
              bubbles: true,
              detail: { variantId: item.variant_id },
            })
          );
        });

        if (theme.settings && theme.settings.afterAddToCart === "drawer") {
          document.dispatchEvent(
            new CustomEvent("dispatch:cart-drawer:open", { bubbles: true })
          );
        }

        this.cta.textContent = this.ctaAdded;
        setTimeout(() => {
          this.cta.disabled = false;
          this.cta.textContent = this.ctaLabel;
        }, 2000);
      } catch (err) {
        console.error(err);
        this.setErrorState(err.message || this.ctaError);
        this.cta.disabled = false;
        this.cta.textContent = this.ctaLabel;

        document.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: true,
            detail: { error: err.message },
          })
        );
      }
    }

    setErrorState(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message || "";
    }
  }

  customElements.define("pack-builder", PackBuilder);
}
